import AddItemForm from '@components/drawers/AddItemForm';
import CustomDrawer from '@components/drawers/CustomDrawer';
import GridListSubheader from '@components/GridListSubheader';
import ItemWithSummaryDrag from '@components/ItemWithSummaryDrag';
import { graphQlClient } from '@config/graphqlClient';
import AreasGraphQL from '@graphql/area.queries';
import IntegrationsGraphQL from '@graphql/integration.queries';
import OrganizationsGraphQL from '@graphql/organization.queries';
import { useLoader } from '@hooks/useLoader';
import { useLocationQuery } from '@hooks/useLocationQuery';
import { useTranslation } from '@hooks/useTranslation';
import {
  Area,
  createOrUpdateAreaRequest,
  deleteAreaRequest,
  getAreasRequest,
  reorderAreasRequest
} from '@models/area.model';
import { DrawerType, EntityData, SnackType } from '@models/common.model';
import { createOrUpdateOrganizationRequest } from '@models/organization.model';
import { UserRoles } from '@models/user.model';
import { WolfieFTUEStatus } from '@models/wolfie.model';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { setWolfieFTUEStatus } from '@redux/reducers/wolfie.reducer';
import { RootState } from '@redux/store';
import { checkForIntegrations } from '@utils/checkForIntegrations';
import { notValidDrop, DropResult } from '@utils/notValidDrop';
import { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';

const AreaList: React.FC = () => {
  const dispatch = useAppDispatch();
  const locAM = useTranslation('actionMessages');
  const localeAreas = useTranslation('areas');
  const localeCommon = useTranslation('common');
  const history = useHistory();
  const { setLoading } = useLoader();

  const { role } = useAppSelector((state: RootState) => state.user);

  const [areas, setAreas] = useState<any[]>([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerType, setDrawerType] = useState<DrawerType>(DrawerType.ADD);
  const [entityData, setEntityData] = useState<EntityData>({
    areaId: '',
    name: '',
    description: '',
    color: '',
  });
  const [hasCalendar, setHasCalendar] = useState(false);
  const [hasStorage, setHasStorage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isGridView, setIsGridView] = useState(true);
  const query = useLocationQuery();
  const [autoOpenDrawer, setAutoOpenDrawer] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [recentlyDroppedId, setRecentlyDroppedId] = useState<string | null>(null);

  const { organizationId } = useAppSelector((state: RootState) => state.organization);

  // TODO: This is to avoid the FTUE since it is currently disabled
  // const wolfieFTUEStatus = WolfieFTUEStatus.IGNORE;

  // Track previous organizationId to detect real org switches vs initial load
  const prevOrgIdRef = useRef<string | null | undefined>(undefined);

  // Initial data fetch - only depends on query changes, not organizationId
  useEffect(() => {
    const openDrawer = query.get('openDrawer') === 'true';
    setAutoOpenDrawer(openDrawer);
    setDataLoaded(false);
    getAreas();
    getIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Handle organization switches - only fetch when org actually changes after initial mount
  useEffect(() => {
    // Skip on initial mount
    if (prevOrgIdRef.current === undefined) {
      prevOrgIdRef.current = organizationId;
      return;
    }

    // Only refetch if organizationId actually changed
    if (prevOrgIdRef.current !== organizationId) {
      prevOrgIdRef.current = organizationId;
      setDataLoaded(false);
      getAreas();
      getIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // Auto-open drawer after data is loaded
  useEffect(() => {
    if (autoOpenDrawer && dataLoaded) {
      drawerNewOpen();
      setAutoOpenDrawer(false); // Reset to prevent multiple opens
    }
  }, [autoOpenDrawer, dataLoaded]);

  // TODO: Remove??
  // useEffect(() => {
  //   if (wolfieFTUEStatus === WolfieFTUEStatus.SHOW) {
  //     setIsGenerating(true);
  //     dispatch(setWolfieEnabled(true));
  //     dispatch(setWolfieStatus(WolfieStatus.RESEARCHING));
  //     dispatch(setWolfieMinimized(false));
  //     dispatch(setWolfieVideoMode(true));
  //   }
  // }, [wolfieFTUEStatus]);

  const handleToggleView = () => {
    setIsGridView(!isGridView);
  };

  const handleAreaClick = async (id: string, e?: React.MouseEvent) => {
    const url = `/services?areaId=${id}`;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  };

  // const handleIgnoreFTUE = () => {
  //   updateOrganizationIgnore();
  //   dispatch(setWolfieFTUEStatus(WolfieFTUEStatus.IGNORE));
  // };

  // const handleShowFTUE = () => {
  //   dispatch(setWolfieFTUEStatus(WolfieFTUEStatus.SHOW));
  // };

  // DRAWER HANDLE *********************
  const drawerNewOpen = () => {
    setDrawerType(DrawerType.ADD);
    setShowDrawer(true);
  };

  const drawerEditOpen = (id: string) => {
    setDrawerType(DrawerType.EDIT);
    setShowDrawer(true);
    const areaToEdit = areas.find((area: Area) => area.areaId === id);
    if (areaToEdit) {
      setEntityData(areaToEdit);
    }
  };

  const drawerClose = (): void => {
    setShowDrawer(false);
  };

  const updateOrganizationIgnore = async () => {
    if (![UserRoles.SUPER_ADMIN, UserRoles.OWNER, UserRoles.ADMIN].includes(role as UserRoles)) {
      return;
    }
    try {
      const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
        OrganizationsGraphQL.mutations.updateOrganization,
        {
          organization: {
            organizationId: organizationId || '',
            lastProfileUpdate: new Date().toISOString(),
          },
        },
      );
      if (data?.updateOrganization?.organizationId) {
        dispatch(setOrganizationData(data.updateOrganization));
      }
    } catch {}
  };

  // Drag and Drop
  const handleDragEnd = async (result: DropResult) => {
    if (notValidDrop(result)) return;
    const newAreas: Area[] = [...areas];

    // Remove the item from its current position
    const [movedItem] = newAreas.splice(result.source.index, 1);

    // Insert the item at the new position
    newAreas.splice(result.destination!.index, 0, movedItem);

    const areasIds = newAreas.map((item: Area) => item.areaId);

    try {
      const data: reorderAreasRequest = await graphQlClient.request(AreasGraphQL.mutations.reorderAreas, {
        areas: { areasIds },
      });
      if (data?.reorderAreas.messages[0] === 'Update successful') {
        setAreas(newAreas);

        // Set recently dropped item and clear after 5 seconds
        setRecentlyDroppedId(result.draggableId);
        setTimeout(() => {
          setRecentlyDroppedId(null);
        }, 5000);

        dispatch(appendActionMessage({ message: locAM['areasReorderedSuccessfully'], type: SnackType.SUCCESS }));
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  // CRUD Operations (incl Drawer save new/edit)
  // TODO: Clean up try/catches and consolidate
  // TODO: Cleanup FTUE
  const getAreas = async (justCompleted: boolean = false) => {
    try {
      dispatch(setWolfieFTUEStatus(null));
      setLoading(true);
      setHasError(false);
      const data: getAreasRequest = await graphQlClient.request(AreasGraphQL.queries.getAreas);
      setAreas(data.getAreas.sort((a: any, b: any) => (a.order > b.order ? 1 : -1)));

      if (
        data.getAreas.length === 0
        //&& enabledWolfie
      ) {
        // TODO: Adjust criteria for ftue
        // We show FTUE if there are no areas, no profile updated and the user has not ignored it
        // if (!onboardingDetails?.ftueCompleted && !(wolfieFTUEStatus === WolfieFTUEStatus.IGNORE || justCompleted)) {
        //   dispatch(setWolfieFTUEStatus(WolfieFTUEStatus.MESSAGE));
        // } else {
        //   dispatch(setWolfieFTUEStatus(null));
        //   dispatch(setWolfieEnabled(true));
        //   dispatch(setWolfieMinimized(false));
        // }
        // This one was causing an annoying experience
        // history.push('/areas?openDrawer=true');
      }
      setDataLoaded(true);
    } catch (e: any) {
      setHasError(true);
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const deleteResult: deleteAreaRequest = await graphQlClient.request(AreasGraphQL.mutations.deleteArea, { id });
      if (deleteResult?.deleteArea?.affected > 0) {
        setAreas(areas.filter((area: Area) => area.areaId !== id));
        dispatch(appendActionMessage({ message: locAM['areaDeletedSuccessfully'], type: SnackType.SUCCESS }));
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      drawerClose();
    }
  };

  const handleDrawerSave = async (entityData: EntityData) => {
    if (!entityData.name || !entityData.description || !entityData.color) {
      dispatch(
        appendActionMessage({
          message: locAM['nameDescriptionColorRequired'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }
    const isEdit = drawerType === DrawerType.EDIT;

    if (isEdit && !entityData.areaId) {
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }
    if (!entityData.name || !entityData.description || !entityData.color) {
      dispatch(
        appendActionMessage({
          message: locAM['fillAreaFields'],
          type: SnackType.ERROR,
        }),
      );
    }
    setSaving(true);

    try {
      const query = isEdit ? AreasGraphQL.mutations.updateArea : AreasGraphQL.mutations.createArea;
      const params = {
        area: {
          areaId: entityData.areaId,
          name: entityData.name,
          description: entityData.description,
          color: entityData.color,
        },
      };
      if (!isEdit) {
        delete params.area.areaId;
      }
      const data: createOrUpdateAreaRequest = await graphQlClient.request(query, params);
      if (data?.createArea?.areaId || data?.updateArea?.areaId) {
        if (isEdit && data?.updateArea) {
          const existingArea = areas.find((a: Area) => a.areaId === entityData.areaId);
          const newArea: Area = {
            ...data.updateArea,
            services: existingArea?.services || data.updateArea.services,
          };
          setAreas(
            [...areas.filter((area: Area) => area.areaId !== entityData.areaId), newArea].sort((a, b) =>
              a.order > b.order ? 1 : -1,
            ),
          );
        } else {
          setAreas([...areas, data.createArea]);
        }
        dispatch(
          appendActionMessage({
            message: isEdit ? locAM['areaUpdatedSuccessfully'] : locAM['areaCreatedSuccessfully'] || '',
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setSaving(false);
      drawerClose();
    }
  };

  const getIntegrations = async () => {
    try {
      const { calendar, storage } = checkForIntegrations(
        await graphQlClient.request(IntegrationsGraphQL.queries.getIntegrations),
      );
      setHasCalendar(calendar);
      setHasStorage(storage);
    } catch (e: any) {
      console.log('error', e);
    }
  };

  // TODO: Reenable this once the FTUE is enabled
  // if (wolfieFTUEStatus === WolfieFTUEStatus.MESSAGE) {
  //   return (
  //     <Box className="flex w-full">
  //       <Box className="w-full h-full flex items-center">
  //         <Box className="flex flex-wrap gap-8 align-items justify-center mx-auto" sx={{ maxWidth: '600px' }}>
  //           <Typography variant="h3semibold" className="text-center w-full" component="h3">
  //             {localeWolfie['ftueMessage1']}
  //           </Typography>
  //           <Typography variant="body16" className="text-center" component="p">
  //             {localeWolfie['ftueMessage2']}
  //           </Typography>
  //           <Typography variant="body16" className="text-center" component="p">
  //             {localeWolfie['ftueMessage3']}
  //           </Typography>
  //           <Typography variant="body16" className="text-center" component="p">
  //             {localeWolfie['ftueMessage4']}
  //           </Typography>
  //           <Typography variant="body16" className="text-center" component="p">
  //             {localeWolfie['ftueMessage5']}
  //           </Typography>
  //           <Box className="flex w-full justify-between mx-8">
  //             <WolfButton color="primary" variant="outlined" onClick={handleIgnoreFTUE}>
  //               {localeWolfie['ftueCancelButton']}
  //             </WolfButton>
  //             <WolfButton color="primary" variant="outlined" onClick={handleShowFTUE}>
  //               {localeWolfie['ftueStartButton']}
  //             </WolfButton>
  //           </Box>
  //         </Box>
  //       </Box>
  //     </Box>
  //   );
  // }

  return (
    <>
      <Box className={`flex w-full mb-28`}>
        <Box className="w-full">
          <Box className="sticky top-0 z-30 bg-backgroundUI">
            <GridListSubheader
              title={localeAreas['title']}
              titleLink={'/areas'}
              buttonText={role !== UserRoles.USER ? localeAreas['addNew'] : null}
              isGridView={isGridView}
              onToggleView={handleToggleView}
              onDrawerOpen={drawerNewOpen}
              icon="area"
            />
          </Box>
          {autoOpenDrawer && !dataLoaded && (
            <Box className="flex justify-center items-center h-32">
              <CircularProgress size={24} />
              <Typography variant="body16semibold" className="ml-2">
                Loading...
              </Typography>
            </Box>
          )}
          {areas.length === 0 ? (
            <Box className="flex justify-center items-center h-full w-full">
              <Typography variant="h4semibold">
                {hasError ? localeCommon['requestError'] : localeAreas['emptyAreaMessage']}
              </Typography>
            </Box>
          ) : (
            <ItemWithSummaryDrag
              isGridView={isGridView}
              elements={areas}
              elementIdName="areaId"
              showDrag={true}
              hasCalendar={hasCalendar}
              hasStorage={hasStorage}
              recentlyDroppedId={recentlyDroppedId}
              handleDragEnd={handleDragEnd}
              drawerEditOpen={drawerEditOpen}
              handleCardClick={handleAreaClick}
            />
          )}
        </Box>
      </Box>

      <CustomDrawer
        isOpen={showDrawer}
        onClose={drawerClose}
        title={drawerType === 'edit' ? localeAreas['edit'] : localeAreas['addNew']}>
        <AddItemForm
          saving={saving}
          onClose={drawerClose}
          handleSave={handleDrawerSave}
          handleDelete={handleDelete}
          drawerType={drawerType}
          initialData={entityData}
          offerAreaColors={true}
          saveBtnText={drawerType === DrawerType.EDIT ? localeAreas['editArea'] : localeAreas['createArea']}
          existingColors={areas
            .filter((area: Area) => area.areaId !== entityData.areaId)
            .map((area: Area) => area.color)}
        />
      </CustomDrawer>
    </>
  );
};

export default AreaList;
